"use client";

import { useState, useCallback, useRef } from "react";

import {
  SUPPORTED_EXTENSIONS_LABEL,
  UPLOAD_ACCEPT,
  classifyUploadFile,
  isImageFile,
  type ScoreFormat,
  type UploadFileKind,
} from "@/lib/upload/score-file";

// ─── File utilities ───

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

// ─── Types ───

/**
 * How the upload is processed — and *only* that.
 *
 * - `score`  : one file that already encodes the notes, sent as-is to the
 *              analyzer. MIDI and MusicXML both land here; which one it is
 *              lives in `ScoreFileUpload["format"]`.
 * - `images` : pictures of a score, which must be read by OCR first.
 *
 * The two questions used to share the single value `"midi"`, which then had to
 * mean "this file is a MIDI" and "take the direct path" at once. A MusicXML
 * takes the same path without being a MIDI, so the questions are asked
 * separately now: `mode` for the pipeline, `format` for the file.
 */
export type UploadMode = "score" | "images";

export interface ScoreFileUpload {
  mode: "score";
  /** Which family the file belongs to. Drives wording, never routing. */
  format: ScoreFormat;
  file: File;
}

export interface ImagesUpload {
  mode: "images";
  files: File[];
}

export type Upload = ScoreFileUpload | ImagesUpload;

/**
 * Collapses the two axes into the single question a view asks: "what am I
 * looking at?". Photos and PDFs answer the same thing — a picture of a score —
 * which is why the images path has no format of its own.
 */
export function uploadSource(upload: Upload): UploadFileKind {
  return upload.mode === "images" ? "image" : upload.format;
}

// ─── Component ───

interface FileDropZoneProps {
  upload: Upload | null;
  onScoreFile: (f: File, format: ScoreFormat) => void;
  onImageFiles: (files: File[]) => void;
  onClear: () => void;
  isLoading: boolean;
  error: string | null;
}

export function FileDropZone({ upload, onScoreFile, onImageFiles, onClear, isLoading, error }: FileDropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = useCallback(
    (fileList: FileList | File[]) => {
      const files = Array.from(fileList);
      if (files.length === 0) return;

      if (files.length === 1) {
        const kind = classifyUploadFile(files[0]);

        if (kind === "midi" || kind === "musicxml") {
          onScoreFile(files[0], kind);

          return;
        }
      }


      const images = files.filter(isImageFile);
      if (images.length > 0) {
        onImageFiles(images);
      }
    },
    [onScoreFile, onImageFiles],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) handleFiles(e.target.files);
    },
    [handleFiles],
  );

  const hasUpload = upload !== null;
  const imageCount = upload?.mode === "images" ? upload.files.length : 0;

  return (
    <div className="flex-1 flex flex-col items-center justify-center">
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => !isLoading && inputRef.current?.click()}
        className="w-full max-w-sm aspect-[4/3] rounded-xl flex flex-col items-center justify-center gap-4 cursor-pointer transition-all duration-200"
        style={{
          border: `2px dashed ${isDragging ? "var(--color-accent)" : "rgba(255,255,255,0.08)"}`,
          backgroundColor: isDragging ? "rgba(139,92,246,0.06)" : "rgba(255,255,255,0.02)",
          opacity: isLoading ? 0.6 : 1,
          pointerEvents: isLoading ? "none" : "auto",
        }}
      >
        {/* Icon */}
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center transition-colors duration-200"
          style={{
            backgroundColor: hasUpload ? "var(--color-violet-soft)" : "rgba(255,255,255,0.04)",
          }}
        >
          {isLoading ? (
            <svg className="animate-spin" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-accent)" strokeWidth="1.5">
              <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
            </svg>
          ) : hasUpload ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 12l2 2 4-4" />
              <circle cx="12" cy="12" r="9" />
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#5c5672" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          )}
        </div>

        {isLoading ? (
          <div className="text-center">
            <p className="text-sm text-text-secondary">
              {upload?.mode === "score" ? "Inspection du fichier..." : "Preparation..."}
            </p>
          </div>
        ) : upload?.mode === "score" ? (
          <>
            <div className="text-center">
              <p className="text-sm font-semibold text-text-primary">{upload.file.name}</p>
              <p className="text-[11px] text-text-muted mt-1">
                {formatFileSize(upload.file.size)}, {upload.format === "musicxml" ? "MusicXML" : "MIDI"}
              </p>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); onClear(); }}
              className="text-[11px] text-accent-bright hover:text-white transition-colors cursor-pointer"
            >
              Changer de fichier
            </button>
          </>
        ) : upload?.mode === "images" ? (
          <>
            <div className="text-center">
              <p className="text-sm font-semibold text-text-primary">
                {imageCount === 1 && upload.files[0].type === "application/pdf"
                  ? upload.files[0].name
                  : `${imageCount} ${imageCount === 1 ? "image" : "images"}`}
              </p>
              <p className="text-[11px] text-text-muted mt-1">
                {imageCount === 1 && upload.files[0].type === "application/pdf"
                  ? formatFileSize(upload.files[0].size) + ", PDF"
                  : upload.files.map((f) => f.name).join(", ")}
              </p>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); onClear(); }}
              className="text-[11px] text-accent-bright hover:text-white transition-colors cursor-pointer"
            >
              Changer de fichiers
            </button>
          </>
        ) : (
          <>
            <div className="text-center">
              <p className="text-sm text-text-secondary">
                Glisse un fichier MIDI, MusicXML, PDF ou des images de partition
              </p>
              <p className="text-[11px] text-text-muted mt-1">
                ou clique pour parcourir
              </p>
            </div>
            <span className="text-[10px] text-text-muted/60">{SUPPORTED_EXTENSIONS_LABEL}</span>
          </>
        )}

        <input
          ref={inputRef}
          type="file"
          accept={UPLOAD_ACCEPT}
          multiple
          className="hidden"
          onChange={handleFileInput}
        />
      </div>

      {error && (
        <p className="text-[11px] text-err mt-3">{error}</p>
      )}
    </div>
  );
}
